import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Download, ShieldCheck, FileText, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { auditApi } from '../services/api';
import { Card, SectionHeader, Loader } from '../components/ui';
import { useStore } from '../store';
import toast from 'react-hot-toast';

const BLUE = '#4a9eff';
const GREEN = '#22c55e';
const RED = '#ff5c5c';
const AMBER = '#ffb800';

const FRAMEWORKS = [
  { id: 'SOC2_CC7',      label: 'SOC 2 · CC7',      desc: 'Change management + access controls' },
  { id: 'CSRD_ARTICLE_8',label: 'CSRD · Article 8', desc: 'Sustainability disclosures' },
  { id: 'ISO_27001_A12', label: 'ISO 27001 · A.12', desc: 'Operations security' },
];

export default function AuditLogPage() {
  const { activeTenantId } = useStore();
  const [filters, setFilters] = useState({ action: '', userId: '', resourceType: '' });
  const [wizard, setWizard] = useState(null);   // null | {framework, from, to}

  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ['audit-log', activeTenantId, filters],
    queryFn: () => auditApi.list({ ...cleaned(filters), limit: 200 }).then((r) => r.data),
    retry: false,
  });
  const { data: integrity } = useQuery({
    queryKey: ['audit-integrity', activeTenantId],
    queryFn: () => auditApi.integrity(5000).then((r) => r.data),
    staleTime: 5 * 60_000, retry: false,
  });
  const runRetention = useMutation({
    mutationFn: () => auditApi.runRetention(),
    onSuccess: (r) => { const d = r.data; toast.success(`Retention pass: financial ${d.financial}, security ${d.security}, default ${d.default} rows archived`); },
    onError: () => toast.error('Retention pass failed'),
  });

  if (isLoading) return <Loader />;

  return (
    <div>
      <SectionHeader title="Audit & Compliance"
        subtitle="Every mutation writes a hash-chained row here. Filter, export, or generate a compliance PDF." />

      {/* Integrity banner */}
      {integrity && (
        <Card style={{
          padding: 12, marginBottom: 16,
          borderLeft: `4px solid ${integrity.status === 'OK' ? GREEN : RED}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {integrity.status === 'OK'
              ? <CheckCircle2 size={16} color={GREEN} />
              : <AlertTriangle size={16} color={RED} />}
            <div style={{ flex: 1 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 12 }}>
                Hash-chain integrity: {integrity.status}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, marginTop: 2, fontFamily: 'monospace' }}>
                {integrity.rowsChecked.toLocaleString()} rows verified · {integrity.breaksFound} breaks · head: {integrity.chainHead || '(none)'}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto auto', gap: 12, alignItems: 'end' }}>
          <Field label="Action" value={filters.action} onChange={(v) => setFilters({ ...filters, action: v })}
            placeholder="e.g. LOGIN_SUCCESS, BUDGET_UPDATED" />
          <Field label="User ID" value={filters.userId} onChange={(v) => setFilters({ ...filters, userId: v })}
            placeholder="user id" />
          <Field label="Resource type" value={filters.resourceType} onChange={(v) => setFilters({ ...filters, resourceType: v })}
            placeholder="e.g. tenant, budget" />
          <button onClick={() => refetch()} style={btn(BLUE)}>
            <RefreshCw size={13} /> Refresh
          </button>
          <a href={auditApi.exportCsvUrl()} target="_blank" rel="noreferrer" style={{ ...btn(GREEN), textDecoration: 'none' }}>
            <Download size={13} /> CSV
          </a>
        </div>
      </Card>

      {/* Compliance wizard */}
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <ShieldCheck size={16} color={BLUE} />
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>Generate compliance report</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {FRAMEWORKS.map((f) => (
            <button key={f.id} onClick={() => setWizard({ framework: f.id, from: defaultFrom(), to: defaultTo() })}
              style={{
                textAlign: 'left', padding: 14, borderRadius: 8,
                background: wizard?.framework === f.id ? 'rgba(74,158,255,0.12)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${wizard?.framework === f.id ? '#4a9eff99' : 'rgba(255,255,255,0.1)'}`,
                cursor: 'pointer',
              }}>
              <div style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{f.label}</div>
              <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, marginTop: 4 }}>{f.desc}</div>
            </button>
          ))}
        </div>
        {wizard && (
          <div style={{ marginTop: 14, padding: 14, borderRadius: 8, background: 'rgba(74,158,255,0.05)',
                        border: '1px solid rgba(74,158,255,0.2)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, alignItems: 'end' }}>
              <Field label="From" type="date" value={wizard.from} onChange={(v) => setWizard({ ...wizard, from: v })} />
              <Field label="To"   type="date" value={wizard.to}   onChange={(v) => setWizard({ ...wizard, to: v })} />
              <a href={auditApi.complianceUrl(wizard.framework, wizard.from, wizard.to)}
                 target="_blank" rel="noreferrer" style={{ ...btn(BLUE), textDecoration: 'none' }}>
                <FileText size={13} /> Download PDF
              </a>
            </div>
          </div>
        )}
      </Card>

      {/* Retention */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={() => { if (window.confirm('Run retention pass now? Rows older than policy age move to archive.')) runRetention.mutate(); }}
          style={btn(AMBER)}>
          Run retention pass
        </button>
      </div>

      {/* Log table */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '160px 140px 130px 130px 1fr 160px',
                      padding: '10px 14px', background: 'rgba(74,158,255,0.06)', fontSize: 11, fontWeight: 700,
                      color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          <div>Time</div><div>Action</div><div>User</div><div>Resource</div><div>Details</div><div>Hash (head)</div>
        </div>
        {rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
            No audit rows match the current filters.
          </div>
        ) : rows.map((r) => (
          <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '160px 140px 130px 130px 1fr 160px',
                        padding: '10px 14px', borderTop: '1px solid rgba(255,255,255,0.05)', fontSize: 12,
                        alignItems: 'center' }}>
            <div style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(255,255,255,0.55)' }}>
              {r.timestamp ? new Date(r.timestamp).toISOString().slice(0, 19).replace('T', ' ') : ''}
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#4a9eff' }}>{r.action}</div>
            <div style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(255,255,255,0.6)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.userId}</div>
            <div style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>
              {r.resourceType}{r.resourceId ? '/' + r.resourceId.slice(0, 8) : ''}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.details}</div>
            <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(255,255,255,0.4)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.currentHash ? r.currentHash.slice(0, 12) + '…' : ''}
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 12,
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
          color: '#fff', boxSizing: 'border-box' }} />
    </div>
  );
}
const btn = (c) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
  background: 'rgba(255,255,255,0.04)', border: `1px solid ${c}44`, color: c, cursor: 'pointer',
});
function cleaned(obj) {
  const out = {};
  Object.keys(obj).forEach((k) => { if (obj[k] && obj[k].trim() !== '') out[k] = obj[k]; });
  return out;
}
function defaultFrom() {
  const d = new Date(); d.setMonth(d.getMonth() - 3);
  return d.toISOString().slice(0, 10);
}
function defaultTo() { return new Date().toISOString().slice(0, 10); }
